import type { DirectoryFolder } from "@/api";

// 디렉터리 트리 노드의 종류. Files 모드가 노드를 펼쳤을 때 무엇을 보일지 결정한다.
// - group: 하위 폴더가 있는 그룹 폴더(펼쳐서 하위 탐색).
// - episodeVideo: 카메라 영상 채널 leaf. 에피소드 목록을 보이고, 선택하면 그 카메라 단일 영상.
// - episodeParquet: parquet 데이터 채널 leaf. 에피소드 목록을 보이고, 선택하면 전체 에피소드 뷰.
// - files: 그 밖의 leaf(meta·metadata·extra 전용·미상). 직속 파일 목록을 보인다.
//
// 종류와 무관하게 **모든 폴더가 직속 파일을 함께 나열한다.** extra 파일은 어느 폴더에든 있을
// 수 있다(`meta/` 의 변환 사이드카, 채널 폴더 옆 메모). 종류가 목록 유무를 정하면 그 파일은
// 어디서도 보이지 않는다. 채널 폴더는 에피소드 목록이 canonical 파일을 대신하므로 extra 만
// 덧붙인다.
export type FolderKind = "group" | "episodeVideo" | "episodeParquet" | "files";

// hatchery-data 의 영상 namespace(카메라 subdir 를 가짐). leaf 면 카메라 영상 채널.
const VIDEO_NAMESPACES: ReadonlySet<string> = new Set(["observation_image", "videos", "masks"]);
// parquet 데이터 namespace. leaf 면 채널 parquet(에피소드당 1파일).
const PARQUET_NAMESPACES: ReadonlySet<string> = new Set([
  "action",
  "observation_vector",
  "auxiliary_vector",
  "data",
]);

/**
 * 트리 노드 종류를 판정한다(pure).
 *
 * BE 가 준 `namespace` 로 판정하고, `namespace` 가 없을 때만 경로의 top segment 로 판정한다.
 * extra 전용 폴더는 canonical 분류가 없고, 구 BE 는 이 필드를 주지 않는다.
 *
 * 영상/parquet 채널로 식별한 canonical leaf 만 에피소드 목록으로 보낸다. **나머지 leaf 는
 * 전부 파일 목록**이다. 알 수 없는 leaf 를 `episodeParquet` 로 추정하면, 그 폴더와 무관한
 * 전체 에피소드 영상 뷰가 열린다. 그러면 사용자는 그 폴더를 봤다고 잘못 믿는다. `metadata/` 의
 * json 을 골랐을 때 카메라 영상이 나타난 원인이 이 경로였다.
 *
 * canonical 과 extra 의 비율로 종류를 정하지 않는다. 어느 카탈로그에 적재됐는지는 목록이
 * 행마다 `kind` 로 밝힌다.
 */
export function classifyFolder(folder: DirectoryFolder): FolderKind {
  const namespace = folder.namespace ?? folder.path.split("/")[0];
  // 하위 폴더가 있으면 그룹(예: observation_image → 카메라들, action → key 들).
  if (folder.folders.length > 0) {
    return "group";
  }
  if (folder.canonical_file_count > 0) {
    if (VIDEO_NAMESPACES.has(namespace)) {
      return "episodeVideo";
    }
    if (PARQUET_NAMESPACES.has(namespace)) {
      return "episodeParquet";
    }
  }
  return "files";
}

// 이 폴더 **바로 아래** 파일 수. 트리의 count 는 자신+하위 재귀 합이므로 하위 폴더 합을
// 빼서 구한다. 그룹·채널 폴더는 이 값으로 직속 파일 목록을 붙일지 요청 없이 정한다.
export function directFileCounts(folder: DirectoryFolder): { canonical: number; extra: number } {
  let canonical = folder.canonical_file_count;
  let extra = folder.extra_file_count;
  for (const child of folder.folders) {
    canonical -= child.canonical_file_count;
    extra -= child.extra_file_count;
  }
  return { canonical: Math.max(0, canonical), extra: Math.max(0, extra) };
}

// 채널 폴더 경로의 마지막 세그먼트(= 카메라/키 이름). episodeVideo 의 directory_channel 매칭 키다.
export function folderChannelName(path: string): string {
  const index = path.lastIndexOf("/");
  return index >= 0 ? path.slice(index + 1) : path;
}
